import { Prompt } from '@weareopenr/component-library';
import { FieldValues, Control, UseFormReset, useFormState } from 'react-hook-form';
import { createPortal } from 'react-dom';
import { useCancelCheck } from 'src/hooks/useCancelCheck';
import { PricingSaveSummary } from '../PricingSaveSummary/PricingSaveSummary';
import { ReactNode } from 'react';

type ConditionalPortalProps = {
  children: ReactNode;
  portalTargetId?: string;
};

const ConditionalPortal = ({ children, portalTargetId }: ConditionalPortalProps) => {
  if (!children) return null;

  if (portalTargetId) {
    const element = document.getElementById(portalTargetId);
    if (element) {
      return createPortal(children, element);
    }
  }

  return <>{children}</>;
};

type PricingSavePortalProps<TFieldValues extends FieldValues = FieldValues> = {
  control: Control<TFieldValues>;
  reset: UseFormReset<TFieldValues>;
  form?: string;
  portalTargetId?: string;
};

export const PricingSavePortal = <TFieldValues extends FieldValues = FieldValues>({
  control,
  reset,
  form,
  portalTargetId,
}: PricingSavePortalProps<TFieldValues>) => {
  const { dirtyFields, isSubmitting } = useFormState({ control });
  const totalChanges = Object.keys(dirtyFields).length;
  const { isCancelCheck, message, handleStay, handleLeave } = useCancelCheck({ totalChanges });

  return (
    <>
      <Prompt
        closePrompt={handleStay}
        open={isCancelCheck}
        buttonHandler={handleLeave}
        buttonText="Leave page"
        backButton
        backButtonText="Review changes"
        promptHeading="Unsaved changes"
        size="small"
      >
        {message}
      </Prompt>
      <ConditionalPortal portalTargetId={portalTargetId}>
        {totalChanges > 0 && (
          <PricingSaveSummary reset={reset} totalChanges={totalChanges} isSubmitting={isSubmitting} form={form} />
        )}
      </ConditionalPortal>
    </>
  );
};
